import type { Sponsor } from "../data/types";
import { fetchSponsors } from "../data/sponsors";
import { motion, useMotionValue, useAnimationFrame } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export default function SponsorsSection() {
  const baseX = useMotionValue(0);
  const [isPaused, setIsPaused] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gap = 10;
  const speed = 50;

  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  useEffect(() => {
    fetchSponsors().then(setSponsors);
  }, []);

  // Sparkles
  const [sparkles, setSparkles] = useState<
    { id: number; x: number; y: number; size: number }[]
  >([]);
  useEffect(() => {
    const interval = setInterval(() => {
      const newSparkle = {
        id: Math.random(),
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2 + Math.random() * 2,
      };
      setSparkles((prev) => [...prev.slice(-8), newSparkle]);
    }, 80);
    return () => clearInterval(interval);
  }, []);

  const measure = () => {
    const el = containerRef.current;
    if (!el) return;
    setContentWidth(el.scrollWidth / 2);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const images = Array.from(el.querySelectorAll("img"));
    let loadedCount = 0;
    const onImgLoad = () => {
      loadedCount += 1;
      if (loadedCount >= images.length) measure();
    };

    if (images.length === 0) measure();
    else {
      images.forEach((img) => {
        if ((img as HTMLImageElement).complete) onImgLoad();
        else {
          img.addEventListener("load", onImgLoad);
          img.addEventListener("error", onImgLoad);
        }
      });
    }

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    setTimeout(measure, 50);

    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", onImgLoad);
        img.removeEventListener("error", onImgLoad);
      });
      ro.disconnect();
    };
  }, []);

  useAnimationFrame((t, delta) => {
    if (!isPaused && contentWidth > 0) {
      const moveBy = (speed * delta) / 1000;
      let next = baseX.get() - moveBy;
      if (Math.abs(next) >= contentWidth + 2 * gap) {
        next += contentWidth + 2 * gap;
        if (Math.abs(next) >= contentWidth) next = next % contentWidth;
      }
      baseX.set(next);
    }
  });

  return (
    <section id="sponsors" className="py-12 relative overflow-x-hidden">
      <div className="absolute inset-0 -z-10 bg-bleuFonce" />

      <div className="container mx-auto px-4 relative z-10">
        <h2 className="text-6xl mb-12 text-center text-orange font-bebas">
          Nos Partenaires
        </h2>

        {/* Carrousel */}
        <motion.div
          className="overflow-hidden relative mb-10 w-full max-w-full"
          onHoverStart={() => setIsPaused(true)}
          onHoverEnd={() => setIsPaused(false)}
        >
          <motion.div
            ref={containerRef}
            className="flex gap-10 w-max"
            style={{ x: baseX }}

            drag="x"
            dragElastic={0.04}
            dragMomentum={false}
            dragConstraints={{ left: -contentWidth, right: 0 }}

            onDragStart={() => setIsPaused(true)}
            onDragEnd={() => setIsPaused(false)}
          >
            {[...sponsors, ...sponsors].map((sponsor: Sponsor, idx) => (
              <div key={idx} className="flex-shrink-0 cursor-pointer">
                {sponsor.website ? (
                  <a
                    href={sponsor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={sponsor.logo}
                      alt={sponsor.name}
                      className="h-24 object-contain"
                    />
                  </a>
                ) : (
                  <img
                    src={sponsor.logo}
                    alt={sponsor.name}
                    className="h-24 object-contain"
                  />
                )}
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Texte sous le carrousel */}
        <div className="max-w-3xl mx-auto space-y-2 text-center text-white">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            className="text-2xl font-bold text-rouge relative"
          >
            <span className="relative inline-block text-rouge">
              Nous sommes à la recherche
              <br />
              de nos supers partenaires pour l’édition 2026 !
              {sparkles.map((s) => (
                <span
                  key={s.id}
                  className="absolute rounded-full bg-white sparkle"
                  style={{
                    left: `${s.x}%`,
                    top: `${s.y}%`,
                    width: s.size,
                    height: s.size,
                  }}
                />
              ))}
            </span>
          </motion.p>

          <p className="text-m">
            L'organisation de ce festival est rendue possible grâce à
            l'engagement et au dévouement des musiciens et bénévoles de
            l'orchestre d'harmonie La Garonne. Entièrement autofinancé, notre
            orchestre s'appuie sur ses propres ressources pour faire vivre ce
            projet.<br />
          </p>
          <p className="text-m">
            Votre soutien, par le biais de partenariats privés ou de dons, est
            essentiel pour nous aider à poursuivre cette belle aventure musicale.
          </p>
        </div>

        {/* Conteneur du bouton + flèche */}
        <div className="mt-10 mb-8 flex flex-col items-center relative">
          {/* Conteneur relatif pour bouton + flèche */}
          <div className="relative flex justify-center items-center w-full max-w-[200px]">

            {/* Bouton HelloAsso */}
            <a
              href="https://www.helloasso.com/associations/association-artistique-des-cheminots-de-toulouse-a-a-c-t/formulaires/2"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-rouge text-bleuFonce font-bebas font-bold text-3xl px-8 py-4 rounded-md shadow-lg hover:bg-orange transition-colors duration-500 flex items-center justify-center w-full"
            >
              Faire un don
            </a>

            {/* Flèche droite — positionnée par rapport au bouton */}
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 302.83"
              className="w-12 h-12 absolute right-[-70px] top-1 -translate-y-1/2"
              fill="none"
              stroke="#FAE810"
              strokeWidth="15"
              strokeMiterlimit="10"
              initial={{ rotate: -4 }}
              animate={{ rotate: 4 }}
              transition={{
                repeat: Infinity,
                repeatType: "reverse",
                duration: 0.5,
                ease: "easeInOut",
              }}
            >
              <g className="scale-x-[-1] origin-center">
                <path d="M38.67,9.1C-21.52,89.74,25.8,173.62,140,211.76c95.16,31.78,204.98,21.18,233.33,18" />
                <polyline points="316 173.49 373.33 229.49 316 293.49" />
              </g>
            </motion.svg>
          </div>
        </div>



        <div className="max-w-3xl mx-auto space-y-2 text-center text-white mb-2">
          <p className="text-m">
            Pour postuler et discuter des conditions du partenariat,&nbsp;
            <a
              href="mailto:fme@lagaronne.fr"
              className="hover:text-orange transition-colors duration-500"
            >
              contactez-nous
            </a>{" "}
            !
          </p>
          <p className="text-xs font-bold text-bleuClair">
            Si vous êtes une entreprise, vous bénéficiez
            <br />
            d’une réduction d’impôt de 60% du montant du don.
            <br />
            Cette réduction est de 66% du montant du don si vous êtes un
            particulier.
          </p>
        </div>
      </div>
    </section>
  );
}